import { type OrderFiscalExecutionFieldsFragment, OrderPaymentMethod } from '@shared/api/graphql/gql/graphql';

export type OrderFiscalizationPhase =
  | 'idle'
  | 'checking_readiness'
  | 'preparing'
  | 'waiting_payment'
  | 'checking_payment_result'
  | 'printing'
  | 'finalizing'
  | 'completed'
  | 'order_cancelled'
  | 'payment_declined'
  | 'payment_unconfirmed'
  | 'cancelling_payment'
  | 'payment_cancelled'
  /** Терминал отменил операцию (код 2000) без следов списания: деньги не двигались. */
  | 'cancelled_on_terminal'
  | 'failed_before_print'
  | 'print_result_unknown'
  | 'payment_without_receipt'
  | 'printed_pending_sync'
  /** Mors читает документы ФН, чтобы узнать, вышел ли чек. */
  | 'checking_receipt'
  /** Деньги получены, касса не отвечает: чек напечатаем, когда она ответит. */
  | 'kkt_unavailable';

export interface OrderFiscalizationState {
  amount: number | null;
  /** Платёж можно отменить на терминале: исход оплаты неясен либо деньги получены без чека. */
  canCancelPayment: boolean;
  /** Сервер доказал отказ «нет бумаги»: доступен повтор печати без повторной оплаты. */
  canRetryReceipt: boolean;
  /** Исход чека неясен: «Напечатать чек» проверяет ФН и печатает, только если чека нет. */
  canResolveReceipt: boolean;
  /** Касса печатает, а ФН прочитать не удалось: вопрос «Чек вышел?». */
  receiptQuestion: boolean;
  /** Попытка закрыта без списания: можно вернуться к оплате, `reset()` её сбросит. */
  canReset: boolean;
  /** Наличная часть отменённого платежа в копейках: её администратор отдаёт клиенту из ящика. */
  cashToReturn: number;
  cancellable: boolean;
  message: string | null;
  phase: OrderFiscalizationPhase;
  shiftAction: 'open' | 'reopen' | null;
  terminalName: string | null;
  technicalDetails: string | null;
  /** Коды чека, которые ГИС МТ не считает нашими; заполняется после печати. */
  unownedMarkingCodes: string[];
}

export const RESETTABLE_PHASES: ReadonlySet<OrderFiscalizationPhase> = new Set([
  'payment_declined',
  'cancelled_on_terminal',
  'failed_before_print',
]);

/** Касса или терминал выполняют команду: крутится спиннер, экран не закрыть. */
export const BUSY_PHASES: ReadonlySet<OrderFiscalizationPhase> = new Set([
  'checking_readiness',
  'preparing',
  'waiting_payment',
  'checking_payment_result',
  'cancelling_payment',
  'printing',
  'finalizing',
  'checking_receipt',
]);

/** Фазы, где кассир смотрит на терминал: показываем карточную часть, а не итог заказа. */
const CARD_AMOUNT_PHASES: ReadonlySet<OrderFiscalizationPhase> = new Set([
  'waiting_payment',
  'checking_payment_result',
  'payment_declined',
  'cancelled_on_terminal',
  'payment_unconfirmed',
  'cancelling_payment',
  'payment_cancelled',
]);

export function createInitialOrderFiscalizationState(): OrderFiscalizationState {
  return {
    amount: null,
    canCancelPayment: false,
    canRetryReceipt: false,
    canResolveReceipt: false,
    receiptQuestion: false,
    canReset: false,
    cashToReturn: 0,
    cancellable: false,
    message: null,
    phase: 'idle',
    shiftAction: null,
    terminalName: null,
    technicalDetails: null,
    unownedMarkingCodes: [],
  };
}

export function displayFiscalizationAmount(
  phase: OrderFiscalizationPhase,
  execution: OrderFiscalExecutionFieldsFragment,
): number {
  const cardAmount = CARD_AMOUNT_PHASES.has(phase)
    ? execution.receipt_plan.payments.find((payment) => payment.method === OrderPaymentMethod.Card)?.amount
    : undefined;
  return cardAmount ?? execution.receipt_plan.total_amount;
}
